// src/components/ChromatogramEditor.js
import React, { useRef, useEffect, useState, useCallback } from 'react';

const CHART_TEMPLATES = {
  hplc: {
    label: 'HPLC Chromatogram', labelDe: 'HPLC-Chromatogramm',
    yLabel: 'Response', xLabel: 'Time (min)', xMax: 10, yMax: 2500000, yStep: 500000,
    peaks: [{ time: 3.36, height: 2400000, width: 0.08, label: 'Main' }],
    color: '#1a237e',
  },
  mass: {
    label: 'Mass Spectrum', labelDe: 'Massenspektrum',
    yLabel: '%', xLabel: 'm/z', xMax: 300, yMax: 100, yStep: 20,
    peaks: [
      { time: 159, height: 100, width: 1.5, label: '159' },
      { time: 166, height: 20,  width: 1,   label: '166' },
      { time: 183, height: 15,  width: 1,   label: '183' },
      { time: 214, height: 10,  width: 1,   label: '214' },
    ],
    color: '#7b1fa2', barMode: true,
  },
  uv: {
    label: 'UV Spectrum', labelDe: 'UV-Spektrum',
    yLabel: 'Absorbance (AU)', xLabel: 'Wavelength (nm)', xMax: 400, yMax: 1.5, yStep: 0.3,
    peaks: [{ time: 214, height: 1.35, width: 8, label: '214nm' }],
    color: '#1565c0',
  },
};

function drawChart(canvas, config, editingIdx) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  const padL = 72, padR = 24, padT = 28, padB = 44;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const { peaks, yMax, xMax, yLabel, xLabel, color, barMode, yStep } = config;

  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = '#e8eaf0'; ctx.lineWidth = 0.6;
  const ySteps = Math.round(yMax / yStep);
  for (let i = 0; i <= ySteps; i++) {
    const y = padT + (plotH / ySteps) * i;
    ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
  }
  for (let i = 0; i <= 8; i++) {
    const x = padL + (plotW / 8) * i;
    ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, padT + plotH); ctx.stroke();
  }
  ctx.strokeStyle = '#333'; ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(padL, padT); ctx.lineTo(padL, padT + plotH); ctx.lineTo(W - padR, padT + plotH);
  ctx.stroke();
  ctx.fillStyle = '#444'; ctx.font = '9px Arial'; ctx.textAlign = 'right';
  for (let i = 0; i <= ySteps; i++) {
    const val = yMax - (yMax / ySteps) * i;
    const y = padT + (plotH / ySteps) * i;
    const fmt = val >= 1000000 ? (val/1000000).toFixed(1)+'M' : val >= 1000 ? (val/1000).toFixed(0)+'K' : val % 1 !== 0 ? val.toFixed(1) : val.toString();
    ctx.fillText(fmt, padL - 4, y + 3);
  }
  ctx.save(); ctx.translate(11, padT + plotH/2); ctx.rotate(-Math.PI/2);
  ctx.textAlign = 'center'; ctx.font = '9px Arial'; ctx.fillStyle = '#555';
  ctx.fillText(yLabel, 0, 0); ctx.restore();
  ctx.textAlign = 'center'; ctx.font = '9px Arial'; ctx.fillStyle = '#444';
  for (let i = 0; i <= 8; i++) {
    const val = (xMax / 8) * i;
    const x = padL + (plotW / 8) * i;
    ctx.fillText(val % 1 !== 0 ? val.toFixed(1) : val, x, padT + plotH + 13);
  }
  ctx.fillText(xLabel, padL + plotW / 2, padT + plotH + 26);
  const tx = (v) => padL + (v / xMax) * plotW;
  const ty = (v) => padT + plotH - (v / yMax) * plotH;
  if (barMode) {
    peaks.forEach((pk, idx) => {
      const x = tx(pk.time), y = ty(pk.height);
      ctx.strokeStyle = idx === editingIdx ? '#FF6B35' : color; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x, ty(0)); ctx.lineTo(x, y); ctx.stroke();
      ctx.fillStyle = idx === editingIdx ? '#FF6B35' : color;
      ctx.font = 'bold 8px Arial'; ctx.textAlign = 'center';
      ctx.fillText(Math.round(pk.time), x, y - 5);
    });
  } else {
    const pts = 600, signal = new Float32Array(pts).fill(0);
    peaks.forEach(pk => {
      const center = (pk.time / xMax) * pts, sigma = (pk.width / xMax) * pts;
      for (let i = 0; i < pts; i++) signal[i] += pk.height * Math.exp(-0.5 * Math.pow((i - center) / sigma, 2));
    });
    ctx.globalAlpha = 0.07; ctx.fillStyle = color;
    ctx.beginPath(); ctx.moveTo(padL, ty(0));
    for (let i = 0; i < pts; i++) ctx.lineTo(padL + (i/pts)*plotW, ty(Math.max(0, signal[i])));
    ctx.lineTo(padL+plotW, ty(0)); ctx.closePath(); ctx.fill(); ctx.globalAlpha = 1;
    ctx.strokeStyle = color; ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i < pts; i++) {
      const x = padL + (i/pts)*plotW, y = ty(Math.max(0, signal[i]));
      i === 0 ? ctx.moveTo(x, ty(0)) : ctx.lineTo(x, y);
    }
    ctx.lineTo(padL+plotW, ty(0)); ctx.stroke();
    peaks.forEach((pk, idx) => {
      const x = tx(pk.time), y = ty(pk.height);
      ctx.fillStyle = idx === editingIdx ? '#FF6B35' : '#333';
      ctx.font = 'bold 8.5px Arial'; ctx.textAlign = 'center';
      ctx.fillText(pk.time.toFixed(2), x, y - 7);
      ctx.strokeStyle = idx === editingIdx ? '#FF6B35' : '#aaa';
      ctx.lineWidth = 0.8; ctx.setLineDash([3,3]);
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, ty(0)); ctx.stroke();
      ctx.setLineDash([]);
    });
  }
  return canvas.toDataURL('image/png');
}

function SingleChart({ chartIdx, config, onChange, lang }) {
  const canvasRef = useRef(null);
  const [editingPeak, setEditingPeak] = useState(0);
  const de = lang === 'de';
  const isMass = config.barMode;
  const redraw = useCallback(() => {
    const url = drawChart(canvasRef.current, config, editingPeak);
    onChange && onChange(url);
  }, [config, editingPeak, onChange]);
  useEffect(() => { redraw(); }, [redraw]);
  const updatePeak = (key, val) => {
    const newPeaks = config.peaks.map((p, i) => i === editingPeak ? { ...p, [key]: parseFloat(val) } : p);
    onChange && onChange(null, { ...config, peaks: newPeaks });
  };
  const addPeak = () => {
    const np = isMass ? { time: 200, height: 30, width: 1.5, label: '' } : { time: 5, height: config.yMax * 0.3, width: 0.2, label: 'Peak' };
    const newPeaks = [...config.peaks, np];
    onChange && onChange(null, { ...config, peaks: newPeaks });
    setEditingPeak(newPeaks.length - 1);
  };
  const removePeak = () => {
    if (config.peaks.length <= 1) return;
    const newPeaks = config.peaks.filter((_, i) => i !== editingPeak);
    onChange && onChange(null, { ...config, peaks: newPeaks });
    setEditingPeak(Math.max(0, editingPeak - 1));
  };
  const pk = config.peaks[editingPeak];
  const xLabel = isMass ? (de ? 'm/z-Wert' : 'm/z value') : (de ? 'Retentionszeit (min)' : 'Retention time (min)');
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={cs.canvasWrap}><canvas ref={canvasRef} width={560} height={200} style={cs.canvas} /></div>
      <div style={cs.peakTabs}>
        {config.peaks.map((_, i) => (
          <button key={i} style={{ ...cs.peakTab, ...(i === editingPeak ? cs.peakTabActive : {}) }} onClick={() => setEditingPeak(i)}>
            {isMass ? `m/z ${Math.round(_.time)}` : `Peak ${i+1} (${_.time.toFixed(2)} min)`}
          </button>
        ))}
        <button style={cs.addPeakBtn} onClick={addPeak}>+</button>
        {config.peaks.length > 1 && <button style={cs.removePeakBtn} onClick={removePeak}>−</button>}
      </div>
      {pk && (
        <div style={cs.sliders}>
          <SliderRow label={xLabel} value={pk.time} min={isMass ? 140 : 0.1} max={isMass ? 300 : config.xMax - 0.1} step={isMass ? 0.5 : 0.05} onChange={v => updatePeak('time', v)} display={isMass ? Math.round(pk.time).toString() : pk.time.toFixed(2)} />
          <SliderRow label={isMass ? (de ? 'Intensität (%)' : 'Intensity (%)') : (de ? 'Peakhöhe' : 'Peak height')} value={pk.height} min={isMass ? 1 : 10} max={config.yMax} step={isMass ? 1 : config.yMax/200} onChange={v => updatePeak('height', v)} display={isMass ? Math.round(pk.height)+'%' : (pk.height >= 1000000 ? (pk.height/1000000).toFixed(2)+'M' : Math.round(pk.height).toString())} />
          {!isMass && <SliderRow label={de ? 'Peakbreite' : 'Peak width'} value={pk.width} min={0.02} max={1.5} step={0.01} onChange={v => updatePeak('width', v)} display={pk.width.toFixed(2)} />}
        </div>
      )}
    </div>
  );
}

function SliderRow({ label, value, min, max, step, onChange, display }) {
  return (
    <div style={cs.sliderRow}>
      <div style={cs.sliderLabel}>{label}</div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(e.target.value)} style={cs.slider} />
      <div style={cs.sliderVal}>{display}</div>
    </div>
  );
}

export default function ChromatogramEditor({ onChange, lang = 'en', initialCharts = [] }) {
  const de = lang === 'de';
  const [charts, setCharts] = useState([
    { ...CHART_TEMPLATES.hplc, title: 'Chromatogram', enabled: true },
  ]);
  const [activeChart, setActiveChart] = useState(0);
  const [images, setImages] = useState({});
  const [useExisting, setUseExisting] = useState(initialCharts?.length > 0);

  useEffect(() => {
    if (initialCharts?.length > 0) {
      onChange && onChange(initialCharts);
    }
  }, []); // eslint-disable-line

  const templateKeys = Object.keys(CHART_TEMPLATES);
  const templateLabels = { hplc: 'HPLC', mass: de ? 'Massenspektrum' : 'Mass Spectrum', uv: 'UV' };

  const addChart = (tplKey) => {
    if (charts.length >= 3) return;
    const tpl = CHART_TEMPLATES[tplKey];
    setCharts(cs => [...cs, { ...tpl, title: tpl.label, enabled: true }]);
    setActiveChart(charts.length);
  };
  const removeChart = (idx) => {
    setCharts(cs => cs.filter((_, i) => i !== idx));
    setActiveChart(Math.max(0, idx - 1));
  };
  const updateChartConfig = (idx, newConfig) => {
    setCharts(cs => cs.map((c, i) => i === idx ? { ...c, ...newConfig } : c));
  };
  const handleChartImage = (idx, imgUrl, newConfig) => {
    if (newConfig) updateChartConfig(idx, newConfig);
    if (imgUrl) {
      const newImgs = { ...images, [idx]: imgUrl };
      setImages(newImgs);
      onChange && onChange(Object.entries(newImgs).map(([i, url]) => ({
        url, title: charts[parseInt(i)]?.title || '',
      })));
    }
  };

  if (useExisting && initialCharts?.length > 0) {
    return (
      <div style={cs.wrap}>
        <div style={{ marginBottom:16, background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:12, padding:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <span style={{ fontSize:12, fontWeight:700, color:'#4ADE80' }}>
              ✓ {de ? 'Vorhandene Diagramme (vom Original)' : 'Existing charts (from original)'}
            </span>
            <button
              onClick={() => { setUseExisting(false); onChange && onChange([]); }}
              style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', color:'#F87171', borderRadius:7, padding:'4px 10px', fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>
              ✕ {de ? 'Ersetzen' : 'Replace with new'}
            </button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {initialCharts.map((ch, i) => ch?.url && (
              <div key={i}>
                {ch.title && <div style={{ fontSize:12, fontWeight:600, color:'rgba(255,255,255,0.7)', marginBottom:4 }}>{ch.title}</div>}
                <img src={ch.url} alt={ch.title || 'Chart'} style={{ width:'100%', borderRadius:6, border:'1px solid rgba(255,255,255,0.08)' }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={cs.wrap}>
      <p style={cs.hint}>
        {de
          ? 'Erstellen Sie bis zu 3 Diagramme. Passen Sie die Peaks mit den Schiebereglern an.'
          : 'Create up to 3 charts. Adjust peaks with sliders. All charts will be embedded in Page 2 of the PDF.'}
      </p>
      <div style={cs.chartTabs}>
        {charts.map((ch, i) => (
          <button key={i} style={{ ...cs.chartTab, ...(i === activeChart ? cs.chartTabActive : {}) }} onClick={() => setActiveChart(i)}>
            📈 {ch.title || `Chart ${i+1}`}
            {charts.length > 1 && i === activeChart && (
              <span style={cs.removeChartBtn} onClick={e => { e.stopPropagation(); removeChart(i); }}>×</span>
            )}
          </button>
        ))}
        {charts.length < 3 && (
          <div style={cs.addChartMenu}>
            {templateKeys.map(k => (
              <button key={k} style={cs.addChartBtn} onClick={() => addChart(k)}>+ {templateLabels[k]}</button>
            ))}
          </div>
        )}
      </div>
      {charts[activeChart] && (
        <>
          <div style={{ marginBottom: 10 }}>
            <label style={cs.titleLabel}>{de ? 'Diagrammtitel' : 'Chart title'}</label>
            <input style={cs.titleInput} value={charts[activeChart].title}
              onChange={e => updateChartConfig(activeChart, { title: e.target.value })}
              placeholder={de ? 'z.B. HPLC-UV/VIS Chromatogramm' : 'e.g. HPLC-UV/VIS Chromatogram'} />
          </div>
          <SingleChart chartIdx={activeChart} config={charts[activeChart]} lang={lang}
            onChange={(imgUrl, newConfig) => handleChartImage(activeChart, imgUrl, newConfig)} />
        </>
      )}
    </div>
  );
}

const cs = {
  wrap:           { width: '100%' },
  hint:           { fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 12, lineHeight: 1.5 },
  chartTabs:      { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' },
  chartTab:       { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 },
  chartTabActive: { background: 'rgba(30,94,255,0.2)', border: '1px solid rgba(30,94,255,0.4)', color: '#fff' },
  removeChartBtn: { fontSize: 14, color: '#F87171', marginLeft: 4, lineHeight: 1 },
  addChartMenu:   { display: 'flex', gap: 6 },
  addChartBtn:    { background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.25)', color: '#34D399', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 700 },
  titleLabel:     { fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 5 },
  titleInput:     { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  canvasWrap:     { background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', marginBottom: 10 },
  canvas:         { display: 'block', width: '100%', height: 'auto' },
  peakTabs:       { display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 },
  peakTab:        { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.55)', padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 600 },
  peakTabActive:  { background: 'rgba(255,107,53,0.15)', border: '1px solid #FF6B35', color: '#FF6B35' },
  addPeakBtn:     { background: 'rgba(30,94,255,0.12)', border: '1px solid rgba(30,94,255,0.25)', color: '#4A9EFF', padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', fontWeight: 700 },
  removePeakBtn:  { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#F87171', padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' },
  sliders:        { display: 'flex', flexDirection: 'column', gap: 7 },
  sliderRow:      { display: 'flex', alignItems: 'center', gap: 10 },
  sliderLabel:    { fontSize: 11.5, color: 'rgba(255,255,255,0.6)', minWidth: 150, flexShrink: 0 },
  slider:         { flex: 1, accentColor: '#1E5EFF', cursor: 'pointer' },
  sliderVal:      { fontSize: 11.5, fontFamily: 'monospace', color: '#4A9EFF', minWidth: 70, textAlign: 'right' },
};
